import { Plus } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent } from "../../../../components/ui/common/card";
import { Label } from "../../../../components/ui/common/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import { validateDecimalNumber } from "../../../../utils/utils";
import { ProductEditViewModel, SalesUnit } from "../types/productEditView.types";
import ProductUnitTable from "./ProductUnitTable";

interface ProductEditUnitFormCardProps {
  vm: ProductEditViewModel;
}

const EYEBROW_CLASS =
  "tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground tw-font-medium";

/**
 * "ADD UNIT SIZES" card for the edit flow — matches the Lovable prototype's
 * section header copy/style, an inline Unit Name / Unit Size / Unit Type /
 * EAN Barcode mini-form plus an Add (or Update, while editing a row) button
 * for the next unit, and each already-persisted unit rendered as its own
 * card below (`ProductUnitTable`, despite the name — restyled to match the
 * prototype's per-unit cards rather than a plain table row). Unlike the
 * register flow's live-editable draft array, each unit here already round-
 * trips through the real API on add/edit/delete — see
 * `useProductEditViewModel.ts` — so the mini-form-then-submit shape is kept
 * rather than making the cards themselves inline-editable.
 */
const ProductEditUnitFormCard = ({ vm }: ProductEditUnitFormCardProps) => {
  const onSubmitUnit = vm.enableSizeEditStatus ? vm.onUpdateUnit : vm.onAddUnit;

  return (
    <Card className="tw-p-0">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-border tw-px-5 tw-py-3">
        <div>
          <span className={EYEBROW_CLASS}>Add unit sizes</span>
          <p className="tw-mt-0.5 tw-text-xs tw-text-muted-foreground">
            One product can carry multiple sales units — add a name, size and image for each.
          </p>
        </div>
      </div>
      <CardContent className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
        <FormProvider {...vm.unitMethods}>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-5 md:tw-items-end">
            <FormInput
              control={vm.unitMethods.control}
              name="sales_unit_name"
              label="Unit Name"
              required
              placeholder="Unit Name"
              errorMessage="Unit name is required"
              className="tw-h-9 tw-text-sm"
              labelClassName="tw-text-xs"
              rules={{ validate: (value?: string) => vm.validateUnitName(value || "") }}
            />
            <FormInputNumber
              control={vm.unitMethods.control}
              name="sales_unit_size"
              label="Unit Size"
              required
              placeholder="Unit Size"
              className="tw-h-9 tw-font-mono tw-text-sm"
              labelClassName="tw-text-xs"
              errorMessage="Unit size is required"
              rules={{ validate: (value: any) => validateDecimalNumber(value) }}
            />
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <Label className="tw-text-xs">Unit Type</Label>
              <Select value={vm.defaultUnitType} onValueChange={vm.onDefaultUnitTypeChange}>
                <SelectTrigger className="!tw-h-9 tw-w-full tw-font-mono tw-text-sm">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {vm.unitTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value} className="tw-font-mono">
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FormInput
              control={vm.unitMethods.control}
              name="ean_barcode"
              label="EAN Barcode"
              placeholder="optional"
              className="tw-h-9 tw-font-mono tw-text-sm"
              labelClassName="tw-text-xs"
            />
            {vm.enableSizeEditStatus ? (
              <Button
                type="button"
                onClick={vm.unitMethods.handleSubmit((data: SalesUnit) => onSubmitUnit(data))}
              >
                Update
              </Button>
            ) : vm.enableUnitSizeAdd ? (
              <Button
                type="button"
                variant="outline"
                className="tw-gap-2"
                onClick={vm.unitMethods.handleSubmit((data: SalesUnit) => onSubmitUnit(data))}
              >
                <Plus className="tw-h-4 tw-w-4" />
                Add
              </Button>
            ) : null}
          </div>
        </FormProvider>

        {vm.addProductUnit.length > 0 ? (
          <ProductUnitTable
            units={vm.addProductUnit}
            onEdit={vm.onEditUnitStart}
            onDelete={vm.onDeleteUnitRequest}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ProductEditUnitFormCard;
